/**
 * 本地错题集与间隔复习（GDD 3.3）
 *
 * 间隔台阶 1 / 3 / 7 / 15 天，答对清零、答错进下一档。
 * 本地先记，联网时再补报到云端（`/v1/wrong-answers/sync`）——
 * 弱网也能复习，这是「离线可玩」承诺的一部分。
 *
 * 纪律：所有时间都通过注入的 `now()` 拿，方便测试与将来做"家长端报告"。
 */

import { REVIEW_STAGE_DAYS } from "@chuandeng/shared";
import { readJson, writeJson, type KeyValueStore } from "./storage.js";

const STORAGE_KEY = "chuandeng.wrong-answers.v1";
const MAX_RECORDS = 300;

export interface WrongAnswerRecord {
  cardId: string;
  /** 当前复习档位（对应 REVIEW_STAGE_DAYS 下标） */
  stage: number;
  /** 下次复习时间（unix 秒） */
  reviewAt: number;
  /** 错了几次（用于家长报告与优先级排序） */
  wrongCount: number;
  /** 已答对清零的记录，仅保留在本地供统计，不同步到云端 */
  cleared: boolean;
  /** 是否已同步到云端 */
  synced: boolean;
}

export class WrongAnswerStore {
  constructor(
    private readonly store: KeyValueStore,
    private readonly now: () => number = () => Math.floor(Date.now() / 1000),
  ) {}

  list(): WrongAnswerRecord[] {
    return readJson<WrongAnswerRecord[]>(this.store, STORAGE_KEY, []);
  }

  private save(records: WrongAnswerRecord[]): void {
    // 只保留最近的一批，避免本地存储无限膨胀
    const trimmed = records
      .slice()
      .sort((a, b) => b.reviewAt - a.reviewAt)
      .slice(0, MAX_RECORDS);
    writeJson(this.store, STORAGE_KEY, trimmed);
  }

  /** 答错：进错题集或重置到第一档 */
  add(cardId: string): WrongAnswerRecord {
    const now = this.now();
    const records = this.list();
    const existing = records.find((r) => r.cardId === cardId);
    const record: WrongAnswerRecord = existing
      ? {
          ...existing,
          stage: 0,
          reviewAt: now + REVIEW_STAGE_DAYS[0]! * 86400,
          cleared: false,
          synced: false,
          wrongCount: existing.wrongCount + 1,
        }
      : {
          cardId,
          stage: 0,
          reviewAt: now + REVIEW_STAGE_DAYS[0]! * 86400,
          cleared: false,
          synced: false,
          wrongCount: 1,
        };
    this.save([...records.filter((r) => r.cardId !== cardId), record]);
    return record;
  }

  /** 复习结果：答对清零，答错进下一档（最末档不再后延） */
  resolve(cardId: string, correct: boolean): WrongAnswerRecord | null {
    const now = this.now();
    const records = this.list();
    const existing = records.find((r) => r.cardId === cardId);
    if (!existing) return null;

    const updated: WrongAnswerRecord = correct
      ? { ...existing, cleared: true, reviewAt: now, synced: false }
      : {
          ...existing,
          stage: Math.min(existing.stage + 1, REVIEW_STAGE_DAYS.length - 1),
          reviewAt:
            now + REVIEW_STAGE_DAYS[Math.min(existing.stage + 1, REVIEW_STAGE_DAYS.length - 1)]! * 86400,
          cleared: false,
          synced: false,
          wrongCount: existing.wrongCount + 1,
        };

    this.save([...records.filter((r) => r.cardId !== cardId), updated]);
    return updated;
  }

  /** 待复习（到期且未清零）：先到期的排前面 */
  due(): WrongAnswerRecord[] {
    const now = this.now();
    return this.list()
      .filter((r) => !r.cleared && r.reviewAt <= now)
      .sort((a, b) => a.reviewAt - b.reviewAt);
  }

  /** 未到期但尚未清零的记录（用于展示"还有几张要复习"） */
  pending(): WrongAnswerRecord[] {
    return this.list().filter((r) => !r.cleared);
  }

  pendingSyncCardIds(): string[] {
    return this.list()
      .filter((r) => !r.synced && !r.cleared)
      .map((r) => r.cardId);
  }

  markSynced(cardIds: readonly string[]): void {
    if (cardIds.length === 0) return;
    const set = new Set(cardIds);
    this.save(this.list().map((r) => (set.has(r.cardId) ? { ...r, synced: true } : r)));
  }

  /** 本地统计：给"家长端只读报告"用 */
  stats(): { total: number; pending: number; due: number; cleared: number } {
    const records = this.list();
    return {
      total: records.length,
      pending: records.filter((r) => !r.cleared).length,
      due: this.due().length,
      cleared: records.filter((r) => r.cleared).length,
    };
  }

  clearAll(): void {
    this.store.remove(STORAGE_KEY);
  }
}
